import { Component, OnInit,ViewChild } from '@angular/core';
import { TradingDataService } from '../../../services/tradingdata.service';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import * as dayjs from 'dayjs';
import * as duration from 'dayjs/plugin/duration';
import * as tz from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';
import * as weekfinder from 'dayjs/plugin/weekOfYear';

dayjs.extend(duration);
dayjs.extend(tz);
dayjs.extend(utc);
dayjs.extend(weekfinder);

@Component({
  selector: 'app-btc-price-tracker',
  templateUrl: './btc-price-tracker.component.html',
  styleUrls: ['./btc-price-tracker.component.css']
})
export class BtcPriceTrackerComponent implements OnInit {
  @ViewChild(BaseChartDirective) chart!: BaseChartDirective;
  btcPrice: number | null = null;
  btcChange: number | null = null;
  btcChange1h: number | null = null;
  ethPrice: number | null = null;
  ethChange: number | null = null;
  ethChange1h: number | null = null;
  solPrice: number | null = null;
  solChange: number | null = null;
  solChange1h: number | null = null;
  currentSymbol = "BTC";
  graphLabel = "";
  graphRefresher: any = null;
  lastRefresh: Date | null = null;
  initialLoad = true;
  isLoading = true;
  token = "";
  dataSet: any = [];

  lineChartData: ChartConfiguration['data'] = {
    datasets: [
      {
        data: [],
        label: '',
        fill: false,
        borderColor: "#4472c4",
        pointBorderColor: '#ccc',
        pointHoverBackgroundColor: '#666',
        pointBackgroundColor:'#ccc',
        pointRadius: 0,
        pointHitRadius: 5,
        borderWidth:2
      },
      {
        data: [],
        pointRadius: 0,
        pointHitRadius: 0,
        borderColor: "#ccc",
        borderWidth: 1
      }
    ],
    labels: []
  };
  delayed = false;
  lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    elements: {
      line: {
        tension: 0.5,
      },
    },
    plugins: {
      legend: {
        position: 'right',
        display: false
      },
      title: {
        text: 'Last Hour (USD)',
        display: false
      },
      tooltip: {
        mode:'nearest',
        backgroundColor: '#f0f0f0',
        borderWidth: 1,
        borderColor:'#ccc',
        bodyColor: '#000',
        titleColor: '#333',
        titleMarginBottom: 7,
        boxWidth: 0,
        boxHeight:0,
        callbacks: {
          //title: function (context) {
          //  console.log('title context',context)
          //  return context[0].label.toString();
          //},
          label: function (context) {
            let label = '';

            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    animation: {
      duration: 500,
      easing: 'easeOutSine'

    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          maxTicksLimit: 6,
          callback: function (value, index, ticks) {
            return null;
            //const tmp = new Date(value)
            //return tmp.toLocaleTimeString('en-us', { timeStyle: 'short', hour12: true })
          }
        }
      },
      y: {
        display: true,
        grid: { display: false },
        beginAtZero: false,
        ticks: {
          maxTicksLimit: 7,
          callback: function (value, index, ticks) {
            return '$' + value.toLocaleString('en-us', { minimumFractionDigits: 0 });
          }
        }
      }
    }
  };
  lineChartType: ChartType = 'line';

  constructor(private tradingService: TradingDataService) { }

  ngOnInit(): void {
    const auth = getAuth();
    onAuthStateChanged(auth, async (user: User | null) => {
      if (!user) {
        console.error('❌ No Firebase user found.');
        this.isLoading = false;
        return;
      }

      try {
        this.token = await user.getIdToken();
        this.handleGraph(this.currentSymbol);
        this.graphRefresher = setInterval(() => {
          this.handleGraph(this.currentSymbol);
        }, 15000)


      } catch (err) {
        console.error('🔥 Error fetching crypto data:', err);
      } finally {
        this.isLoading = false;
      }
    });
  }

  //====================================================
  // main handler function
  //====================================================
  async handleGraph(symbol: string): Promise<string> {

    const data = await this.tradingService.getLatestCryptoPrices(this.token);
    this.dataSet = data;

    // set up the chart labels - take them from the first record in the dataset
    const a = this.dataSet[0].labels.split(',');
    const ashort = [...a];

    // format the tooltip timestamps
    for (let i = 0; i < a.length; i++) {
      const dt = new Date('2025-01-01 ' + a[i]).toISOString();
      const tmp = new Date(dt);
      a[i] = tmp.toLocaleTimeString('en-us', { timeStyle: 'medium', hour12: true });

    }

    // format the axis timestamps
    for (let i = 0; i < ashort.length; i++) {
      const dt = new Date('2025-01-01 ' + ashort[i]).toISOString();
      const tmp = new Date(dt);
      ashort[i] = tmp.toLocaleTimeString('en-us', { timeStyle: 'short', hour12: true });
    }
    //console.log("axis", ashort)
    //console.log("dataset pre", this.dataSet)

    // set the labels collection
    this.lineChartData.labels = a;

    const btc = this.dataSet.find((d: any) => d.coin_id === 'BTC-USD');
    const eth = this.dataSet.find((d: any) => d.coin_id === 'ETH-USD');
    const sol = this.dataSet.find((d: any) => d.coin_id === 'SOL-USD');

    btc.labels! = a;

    if (btc) {
      this.btcPrice = btc.price;
      this.btcChange = btc.change_24h;
      this.btcChange1h = btc.change_1h;
    }
    if (eth) {
      this.ethPrice = eth.price;
      this.ethChange = eth.change_24h;
      this.ethChange1h = eth.change_1h;
    }
    if (sol) {
      this.solPrice = sol.price;
      this.solChange = sol.change_24h;
      this.solChange1h = sol.change_1h;
    }

    const stamp = new Date();
    this.lastRefresh = stamp
    this.toggleGraph(symbol)
    return "";

  }




  toggleGraph(symbol: string): void {
    this.currentSymbol = symbol;
    
    // 
    switch (this.currentSymbol) {
      case 'ETH':
        this.lineChartData.datasets[0].data = this.dataSet[1].values.split(',');
        this.lineChartData.datasets[1].data = this.lineChartData.datasets[0].data.map(r => this.lineChartData.datasets[0].data[0]);
        this.lineChartData.datasets[0].borderColor = (this.dataSet[1].change_1h >= 0) ? '#009900ff' : '#d20000ff';
        this.graphLabel = 'Ethereum Last Hour';
        break;
      case 'SOL':
        this.lineChartData.datasets[0].data = this.dataSet[2].values.split(',');
        this.lineChartData.datasets[1].data = this.lineChartData.datasets[0].data.map(r => this.lineChartData.datasets[0].data[0]);
        this.lineChartData.datasets[0].borderColor = (this.dataSet[2].change_1h >= 0) ? '#009900ff' : '#d20000ff';
        this.graphLabel = 'Solana Last Hour';
        break;
      default: // bitcoin
        this.lineChartData.datasets[0].data = this.dataSet[0].values.split(',');
        this.lineChartData.datasets[1].data = this.lineChartData.datasets[0].data.map(r => this.lineChartData.datasets[0].data[0]);
        this.lineChartData.datasets[0].borderColor = (this.dataSet[0].change_1h >= 0) ? '#009900ff' : '#d20000ff';
        this.graphLabel = 'Bitcoin Last Hour';
        break;
    }
    setTimeout(() => {
      this.chart.update();
    })
  }

  getChangeClass(change: number | null): string {
    if (change === null) return '';
    return change >= 0 ? 'positive' : 'negative';
  }

  
}
